import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * The form's own state, kept here rather than in either app's global store:
 * the customer app has Redux, the professional app does not, and the form
 * bootstraps itself from the server on every mount, so nothing needs to
 * outlive the page.
 *
 * The shape and the setter names match what the hooks already expected, so
 * usePreferences reads the same either way.
 */
const INITIAL = {
  cpDoc: null,
  cpSections: [],
  cpFormVersion: null,
  cpBookingName: '',
  cpEgComment: '',
  cpRound: null,
  cpRoundDate: '',
  cpLoading: true,
  cpSaveState: 'idle',
  cpSection: null,
  cpTab: 0,
  // Categories the couple has ticked but not yet said which playlist of. Held
  // here rather than in the section, because the bottom bar is what has to
  // refuse to move on while one is outstanding.
  cpAwaitingChoice: {},
  cpLocksAt: null,
  cpReopenRequested: null,
  // Only the bride and groom may fill the form. Everyone else on the booking
  // reads it. True by default so a preview, which says nothing about it,
  // behaves as it always has.
  cpCanEdit: true,
  // The brief is not this visitor's to read at all. Distinct from a failed
  // request, which is worth reporting differently.
  cpDenied: false,
  // Why the server refused it, which decides what the page says.
  cpDeniedCode: ''
};
const StoreContext = createContext(null);
const setterName = field => `set${field.charAt(0).toUpperCase()}${field.slice(1)}`;
export const CreativePreferencesStore = ({
  children
}) => {
  const [state, setState] = useState(INITIAL);

  // Setters are built once so they are stable across renders, the way the
  // dispatch-bound setters they replace were.
  const setters = useMemo(() => {
    const out = {};
    Object.keys(INITIAL).forEach(field => {
      out[setterName(field)] = value => setState(prev => prev[field] === value ? prev : {
        ...prev,
        [field]: value
      });
    });
    return out;
  }, []);
  const value = useMemo(() => ({
    ...state,
    ...setters
  }), [state, setters]);
  return React.createElement(StoreContext.Provider, {
    value: value
  }, children);
};

/** Reads every field plus its setter. Fields are named, so callers can destructure. */
export const useCreativePreferencesStore = () => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('CreativePreferences must be rendered inside CreativePreferencesStore');
  }
  return store;
};
